/**
 * Shared pieces of the onboarding screens (plan docs/plans/lms-plan.md 5.7,
 * 12.1 to 12.4, section 13; WP-47; R1, R14): the hire's tab
 * (tabs/onboarding), the admin's runs (tabs/team/onboarding) and the template
 * editor (tabs/manage/onboardingTemplates) all read their words, pills,
 * progress and error copy from here, so the three never drift apart.
 *
 * Class strings follow the design skill (G13): semantic pills always carry a
 * word, primary is chrome, dark: classes on everything.
 */
import { format } from 'date-fns';
import { formatDay } from '../../components/DueBadge';
import { Pill } from '../../components/StatusPill';
import type { PillTone } from '../../components/StatusPill';
import { apiErrorCode, apiErrorMessage } from '../../api/client';
import { credentialProblemOf } from '../../api/onboarding';
import type {
  OnboardingEvidence,
  OnboardingItemKind,
  OnboardingItemResult,
  OnboardingProgress,
  OnboardingResponsible,
  OnboardingRunItem,
  OnboardingRunStatus,
} from '../../api/onboarding';

export { formatDay };

// ---------------------------------------------------------------------------
// Words (12.1)
// ---------------------------------------------------------------------------

/** 12.1's kinds in words. */
export const KIND_LABELS: Readonly<Record<OnboardingItemKind, string>> = {
  external_training: 'External training',
  account_setup: 'Account setup',
  document: 'Document',
  course: 'Learning Center course',
  meeting: 'Meeting',
  custom: 'Other',
};

/** What each kind covers (12.1), shown in the template editor. */
export const KIND_HINTS: Readonly<Record<OnboardingItemKind, string>> = {
  external_training:
    "A site with its own training (CITI, a sponsor portal, a vendor's e-learning). The new team member confirms it and can upload the certificate it produces.",
  account_setup: "A login to be created. The admin creates it and marks the item done.",
  document: "A form to sign or read outside the platform.",
  course: "A Learning Center course. Finishing the course completes the item.",
  meeting: "An orientation session or a meeting to attend.",
  custom: "Anything else on the checklist.",
};

/** 5.7 evidence in words. */
export const EVIDENCE_LABELS: Readonly<Record<OnboardingEvidence, string>> = {
  none: 'No evidence',
  text: 'A written confirmation',
  file: 'A file (PDF, PNG or JPEG)',
};

/** 5.7 responsible in words, as the template editor shows it. */
export const RESPONSIBLE_LABELS: Readonly<Record<OnboardingResponsible, string>> = {
  new_hire: 'The new team member',
  admin: 'An admin',
};

export function KindPill({ kind }: { kind: OnboardingItemKind }) {
  return (
    <Pill tone="gray" dot={false}>
      {KIND_LABELS[kind] ?? kind}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// An item's state (12.3, 12.4)
// ---------------------------------------------------------------------------

export type ItemState = 'todo' | 'overdue' | 'awaiting_verification' | 'verified' | 'done' | 'not_applicable';

const ITEM_STATE_PILL: Readonly<Record<ItemState, { tone: PillTone; word: string }>> = {
  todo: { tone: 'primary', word: 'To do' },
  overdue: { tone: 'red', word: 'Overdue' },
  awaiting_verification: { tone: 'amber', word: 'Awaiting verification' },
  verified: { tone: 'green', word: 'Verified' },
  done: { tone: 'green', word: 'Done' },
  not_applicable: { tone: 'gray', word: 'Not applicable' },
};

/** The state of one run item on `today` (YYYY-MM-DD): the runs.js progressOf rules, per item. */
export function itemStateOf(item: OnboardingRunItem, today: string): ItemState {
  if (item.status === 'not_applicable') return 'not_applicable';
  if (item.status === 'done') {
    if (item.requires_verification) return item.verified_at ? 'verified' : 'awaiting_verification';
    return 'done';
  }
  return item.due_on < today ? 'overdue' : 'todo';
}

export function ItemStatePill({ state, testId }: { state: ItemState; testId?: string }) {
  const p = ITEM_STATE_PILL[state];
  return (
    <Pill tone={p.tone} data-testid={testId}>
      {p.word}
    </Pill>
  );
}

const RUN_STATUS_PILL: Readonly<Record<OnboardingRunStatus, { tone: PillTone; word: string }>> = {
  active: { tone: 'primary', word: 'Active' },
  completed: { tone: 'green', word: 'Completed' },
  cancelled: { tone: 'gray', word: 'Cancelled' },
};

export function RunStatusPill({ status, testId }: { status: OnboardingRunStatus; testId?: string }) {
  const p = RUN_STATUS_PILL[status] ?? { tone: 'gray' as PillTone, word: String(status) };
  return (
    <Pill tone={p.tone} data-testid={testId}>
      {p.word}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// Dates and progress
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

/** Today's calendar date on this device (YYYY-MM-DD). */
export function localToday(now: Date = new Date()): string {
  return format(now, 'yyyy-MM-dd');
}

function utcDay(date: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** 'YYYY-MM-DD' plus n days, as a calendar date. */
export function addDays(date: string, days: number): string {
  const base = utcDay(date);
  if (base === null) return date;
  return new Date(base + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The run's progress counts (the twin of runs.js progressOf, 18.24), for the
 * run as it stands after an act on one of its items. The server's own counts
 * are shown until then; its overdue count uses the network's local date.
 */
export function progressOf(items: OnboardingRunItem[], today: string): OnboardingProgress {
  const p: OnboardingProgress = { total: items.length, done: 0, not_applicable: 0, pending: 0, awaiting_verification: 0, overdue: 0 };
  for (const item of items) {
    if (item.status === 'done') p.done += 1;
    else if (item.status === 'not_applicable') p.not_applicable += 1;
    else p.pending += 1;
    if (item.status === 'done' && item.requires_verification && !item.verified_at) p.awaiting_verification += 1;
    if (item.status === 'pending' && item.due_on < today) p.overdue += 1;
  }
  return p;
}

/** Replace one item of a run view with the item an act answered, and recount (12.3, 12.4). */
export function applyItemResult<T extends { run: { status: OnboardingRunStatus }; items: OnboardingRunItem[]; progress: OnboardingProgress }>(
  view: T,
  result: OnboardingRunItem & Partial<Pick<OnboardingItemResult, 'run_status'>>,
  today: string
): T {
  const items = view.items.map((i) => (i.id === result.id ? { ...i, ...result } : i));
  const status = result.run_status ?? view.run.status;
  return { ...view, run: { ...view.run, status }, items, progress: progressOf(items, today) };
}

export interface WeekGroup {
  /** 1 for the start date's first seven days, 2 for the next seven, and so on. */
  week: number;
  from: string;
  to: string;
  items: OnboardingRunItem[];
}

/**
 * 12.3: the items grouped by due week, counted from the hire's start date
 * (week 1 is the start date and the six days after it), earliest week first;
 * inside a week by due date, then in template order.
 */
export function groupByDueWeek(items: OnboardingRunItem[], startDate: string): WeekGroup[] {
  const start = utcDay(startDate);
  const byWeek = new Map<number, OnboardingRunItem[]>();
  for (const item of items) {
    const due = utcDay(item.due_on);
    const days = start !== null && due !== null ? Math.round((due - start) / DAY_MS) : 0;
    const week = Math.max(0, Math.floor(days / 7)) + 1;
    if (!byWeek.has(week)) byWeek.set(week, []);
    byWeek.get(week)!.push(item);
  }
  return Array.from(byWeek.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([week, list]) => ({
      week,
      from: addDays(startDate, (week - 1) * 7),
      to: addDays(startDate, week * 7 - 1),
      items: list.slice().sort((a, b) => (a.due_on < b.due_on ? -1 : a.due_on > b.due_on ? 1 : a.ordinal - b.ordinal)),
    }));
}

/** An http or https address to link to, or null (a link is never anything else; mdLite's rule). */
export function safeHref(url: string | null | undefined): string | null {
  const text = (url ?? '').trim();
  return /^https?:\/\//i.test(text) ? text : null;
}

/** The host of a URL for the link's label ("myscrs.org"), else the URL. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '') || url;
  } catch {
    return url;
  }
}

/** A person's name, else the email, else a plain word (never an id). */
export function personLabel(p: { full_name?: string | null; email?: string | null } | null | undefined, fallback = 'Unnamed person'): string {
  const name = (p?.full_name ?? '').trim();
  if (name) return name;
  const email = (p?.email ?? '').trim();
  return email || fallback;
}

// ---------------------------------------------------------------------------
// The progress block (12.3 "the progress bar")
// ---------------------------------------------------------------------------

export function ProgressBlock({ progress, testId }: { progress: OnboardingProgress; testId?: string }) {
  const total = Math.max(0, progress.total ?? 0);
  const complete = Math.max(0, (progress.done ?? 0) + (progress.not_applicable ?? 0));
  const pct = total > 0 ? Math.min(100, Math.round((complete / total) * 100)) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm text-gray-700 dark:text-gray-300" data-testid={testId ? `${testId}-count` : undefined}>
          {`${complete} of ${total} items complete`}
        </span>
        {progress.overdue ? <Pill tone="red">{`${progress.overdue} overdue`}</Pill> : null}
        {progress.awaiting_verification ? <Pill tone="amber">{`${progress.awaiting_verification} awaiting verification`}</Pill> : null}
      </div>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        role="progressbar"
        aria-label="Onboarding checklist progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="h-full rounded-full bg-primary-600 dark:bg-primary-400" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error copy (18.24; G14)
// ---------------------------------------------------------------------------

/** G14: what a credential refusal says, in plain words (the server's text carries plan references). */
export const CREDENTIAL_COPY =
  "This looks like a password, PIN, token, key or account number. The checklist keeps confirmations and links only, never a credential: take it out and try again.";

const FIELD_WORDS: Readonly<Record<string, string>> = {
  evidence_text: 'The confirmation',
  reason: 'The reason',
  note: 'The note',
  name: 'The name',
  description: 'The description',
  title: 'the title',
  description_md: 'the description',
  url: 'the link',
};

/** A field the server names ("evidence_text", "items[2].url") in words ("Item 3, the link"). */
export function fieldWords(field: string): string {
  const m = /^items\[(\d+)\]\.?(.*)$/.exec(field);
  if (m) {
    const n = Number(m[1]) + 1;
    const part = FIELD_WORDS[m[2]] ?? m[2];
    return part ? `Item ${n}, ${part}` : `Item ${n}`;
  }
  return FIELD_WORDS[field] ?? field;
}

const CODE_COPY: Readonly<Record<string, string>> = {
  LMS_ONBOARDING_ADMIN_ITEM: "An admin completes this item; it is read-only for you.",
  LMS_ONBOARDING_HIRE_ITEM: "The new team member completes this item. You can verify it or mark it not applicable.",
  LMS_COURSE_ITEM: "This item completes when its course is finished.",
  LMS_ONBOARDING_SELF_VERIFY: "Another admin verifies your own onboarding items.",
};

/** What a failed onboarding request says: the credential copy, a code's own words, else the server's message. */
export function describeOnboardingError(err: unknown, fallback: string): string {
  const credential = credentialProblemOf(err);
  if (credential) return `${fieldWords(credential.field)}: ${CREDENTIAL_COPY}`;
  const code = apiErrorCode(err);
  if (code && CODE_COPY[code]) return CODE_COPY[code];
  return apiErrorMessage(err, fallback);
}

/** A due date in words: "Due 19 Oct 2026". */
export function dueWords(dueOn: string): string {
  const day = formatDay(dueOn);
  return day ? `Due ${day}` : 'No due date';
}
